import React, { useState } from "react";
import { DefaultAndAuthProps } from "model/props";
import PageHeader from "components/pageheader/PageHeader";
import FormButton from "components/form/FormButton";
import TextInput from "components/form/TextInput";
import { Box, Typography, Snackbar, Alert } from "@mui/material";
import cssLayout from "../layout.module.scss";
import css from "./users2screen.module.scss";
import { getLanguageString as _ } from "misc/lang";
import { Paths } from "appConfig";
import { apiUserCreate } from "net/requests";

interface UserFormData {
  identifier: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  location: string;
}

const AddUserScreen: React.FC<DefaultAndAuthProps> = (props) => {
  const { account, home, navigate, admin, loader } = props;

  const [formData, setFormData] = useState<UserFormData>({
    identifier: "",
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    location: "",
  });
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const orgName = home?.name || account?.name || "";
  const title = `${_("user", "add_title_prefix")}${orgName}`;
  const subtitle = _("user", "add_subtitle");

  const handleSave = async () => {
    if (!home) {
      setNotification({
        open: true,
        message: _("user", "error_no_home"),
        severity: "error",
      });
      return;
    }

    if (!formData.identifier.trim()) {
      setNotification({
        open: true,
        message: _("user", "error_identifier_required"),
        severity: "error",
      });
      return;
    }

    try {
      loader.setLoading();
      await apiUserCreate(admin, home.uuid, formData.identifier.trim());
      loader.reset();
      setNotification({
        open: true,
        message: _("user", "create_success"),
        severity: "success",
      });
      setTimeout(() => {
        navigate(Paths.USERS2);
      }, 1000);
    } catch (error: any) {
      loader.reset();
      const errorCode = error?.response?.data?.error;
      console.error("Error creating user:", errorCode);
      setNotification({
        open: true,
        message: _("user", "create_error"),
        severity: "error",
      });
    }
  };

  const handleCancel = () => {
    navigate(Paths.USERS2);
  };

  return (
    <div className={cssLayout.layout}>
      <PageHeader title={title} subtitle={subtitle} />

      <div className={css.contentBox}>
        {/* Basic Information Section */}
        <Box className={css.section}>
          <Typography variant="h6" className={css.sectionTitle}>
            {_("user", "basic_information")}
          </Typography>

          <Box className={css.formGrid}>
            <TextInput
              label={_("user", "identifier_label")}
              placeholder={_("user", "placeholder_identifier")}
              labelOutside={true}
              isRequired={true}
              initialValue={formData.identifier}
              updateValue={(value) =>
                setFormData((prev) => ({ ...prev, identifier: value }))
              }
            />

            <TextInput
              label={_("user", "first_name_label")}
              placeholder={_("user", "placeholder_first_name")}
              labelOutside={true}
              initialValue={formData.firstName}
              updateValue={(value) =>
                setFormData((prev) => ({ ...prev, firstName: value }))
              }
            />

            <TextInput
              label={_("user", "last_name_label")}
              placeholder={_("user", "placeholder_last_name")}
              labelOutside={true}
              initialValue={formData.lastName}
              updateValue={(value) =>
                setFormData((prev) => ({ ...prev, lastName: value }))
              }
            />

            <TextInput
              label={_("user", "email_label")}
              placeholder={_("user", "placeholder_email")}
              labelOutside={true}
              initialValue={formData.email}
              updateValue={(value) =>
                setFormData((prev) => ({ ...prev, email: value }))
              }
            />

            <TextInput
              label={_("user", "phone_number_label")}
              placeholder={_("user", "placeholder_phone_number")}
              labelOutside={true}
              initialValue={formData.phoneNumber}
              updateValue={(value) =>
                setFormData((prev) => ({ ...prev, phoneNumber: value }))
              }
            />

            <TextInput
              label={_("user", "location_label")}
              placeholder={_("user", "placeholder_location")}
              labelOutside={true}
              initialValue={formData.location}
              updateValue={(value) =>
                setFormData((prev) => ({ ...prev, location: value }))
              }
            />
          </Box>
        </Box>
      </div>

      {/* Action Buttons */}
      <Box className={css.actionButtons}>
        <FormButton
          label={_("general", "btn_save")}
          onSubmit={handleSave}
          disabled={!formData.identifier.trim()}
          inline={true}
          variant="contained"
          color="primary"
        />
        <FormButton
          label={_("general", "btn_cancel")}
          onSubmit={handleCancel}
          inline={true}
          variant="outlined"
        />
      </Box>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default AddUserScreen;
